// El estado del lienzo de UN chat: las tandas (de los mensajes, y las que el usuario
// pidió desde el visor) y lo guardado (posiciones + oculto + esas tandas), con lectura
// al cambiar de chat y guardado con el mismo debounce que los mensajes —
// ADR-2026-09-16-el-lienzo-se-guarda-por-chat. Los nodos los arma el lienzo
// (`acomodarLienzo`) porque necesita además las proporciones reales de las fotos, que
// sólo él conoce.
//
// Las tandas del visor (Carrusel, Animar, Expansión sobre una foto del lienzo) no están
// en ningún mensaje: el jsonb del chat es su única fuente
// (ADR-2026-09-16-las-tandas-del-visor-aterrizan-en-el-lienzo). Se guardan APENAS se
// piden, como pendientes con sus recuadros, y se completan con sus ids cuando la promesa
// resuelve. Si el chat se cerró antes —la promesa se fue con la pantalla—, al volver se
// las reanuda buscando lo que salió en la base (`resultadosDeTanda.ts`); una que no
// aparece en media hora se da por perdida
// (ADR-2026-09-16-las-tandas-en-vuelo-se-guardan-y-se-reanudan).
//
// El id del chat se lee del motor (`getChatId`) en cada render: un chat recién empezado
// no tiene id hasta `ensureChatCreated`, que corre ANTES de pegarle al agente, así que
// para cuando hay una generación que dibujar el id ya existe. Si igual no lo hubiera, lo
// movido queda en memoria y se persiste con el primer guardado que encuentre id.
//
// Se llama UNA vez, en `PantallaChatAtlas`, y el lienzo recibe el resultado por prop:
// dos instancias serían dos lecturas de la misma fila y dos "oculto" distintos.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Trazo } from '../../pages/flujos/graph/types';
import { useChatAtlas } from '../ChatAtlasContext';
import { guardarLienzo, leerLienzo } from '../../pages/v2/agentChatApi';
import { useAgentEnv } from '../../pages/v2/agentEnvContext';
import type { DisenoGuardado } from '../../pages/v2/modes/disenoIndumentaria/disenoGuardado';
import type { Posiciones, Punto } from './acomodo';
import {
  LIENZO_VACIO,
  conDiseno,
  conPosiciones,
  conTanda,
  conTandaResuelta,
  normalizarLienzo,
  sinTanda,
  type Anotacion,
  type LienzoGuardado,
  type PedidoGuardado,
  type TandaGuardada,
} from './persistenciaDelLienzo';
import {
  INTERVALO_ESTADO_MS,
  INTERVALO_MS,
  buscarResultadosDeTanda,
  consultaDeTanda,
  consultarEstadoDelPedido,
  decidirReanudacion,
  elegirResultados,
  idsUsados,
  necesitaEstadoDelPedido,
  type EstadoDelPedido,
} from './resultadosDeTanda';
import {
  tandaDeGuardada,
  tandasDeMensajes,
  type NombresDelCatalogo,
  type TandaDelLienzo,
} from './tandasDelLienzo';
import { useEntornoChatAtlas } from '../entornoContext';

const DEBOUNCE_MS = 600;

/** Dónde aterriza una tanda de `total` celdas pedida desde `origenId` (o desde ninguna foto). */
export type Colocador = (origenId: string | undefined, total: number) => Punto;
/**
 * Vuelve a armar el pedido de una tanda pendiente con los MISMOS ids y devuelve su
 * resultado, o `null` si ya no se puede (la celda de origen se fue del lienzo)
 * (ADR-2026-09-18-una-tanda-en-vuelo-se-relanza-si-el-pedido-no-llego).
 */
export type Relanzador = (t: TandaGuardada) => Promise<string[]> | null;

/** Una tanda pedida desde el visor, para colgar del lienzo. */
export interface NuevaTandaDelLienzo {
  titulo: string;
  /** Cuántos recuadros mostrar mientras vuela. */
  total: number;
  ratio: number;
  esVideo: boolean;
  /** La foto del lienzo desde la que se pidió; sin ella (una base descrita del modo Diseño)
   *  la tanda va a la derecha de todo. */
  origenId?: string;
  /**
   * Dónde aterriza, decidido al pedirla (`lugarDeTanda`) y guardado con ella: queda fija
   * ahí y no sigue a la foto de origen (ADR-2026-09-17-la-tanda-aterriza-una-vez). Sin
   * ella la decide el colocador que el lienzo registra (`fijarColocador`), así quien no
   * ve los nodos al pedir (el modo Diseño) igual la tiene
   * (ADR-2026-09-18-las-fotos-no-se-mueven-juntas).
   */
  posicion?: Punto;
  /** El `execution_id` del pedido, cuando quien genera lo conoce: la reanudación es exacta. */
  executionId?: string;
  /** Varios pedidos en una tanda (Mostrar en uso: uno por foto): se reanuda por todos. */
  executionIds?: string[];
  /** Cómo volver a armar el pedido si el backend nunca lo recibió (modo Diseño). */
  pedido?: PedidoGuardado;
  /** Ids de `imagenes_generadas`, ya con archivo: quien la entrega espera el preload. */
  resultado: Promise<string[]>;
}

export interface LienzoDelChat {
  tandas: TandaDelLienzo[];
  lienzo: LienzoGuardado;
  moverNodos: (nuevas: Posiciones) => void;
  setOculto: (oculto: boolean) => void;
  /** Cuelga una tanda del visor: recuadros ya, fotos cuando `resultado` resuelve. Devuelve el id de la tanda. */
  agregarTanda: (nueva: NuevaTandaDelLienzo) => string;
  /**
   * El lienzo registra acá cómo decidir dónde aterriza una tanda nueva (con los nodos
   * que ve en ese momento); `agregarTanda` lo usa cuando la tanda no trae `posicion`.
   */
  fijarColocador: (colocador: Colocador | null) => void;
  /** Quien sabe rearmar un pedido guardado (el modo Diseño) lo registra acá; el reanudador lo usa. */
  fijarRelanzador: (relanzador: Relanzador | null) => void;
  /** El diseño (modo Diseño de indumentaria): se guarda con el mismo debounce que el resto. */
  setDiseno: (fn: (d: DisenoGuardado) => DisenoGuardado) => void;
  /**
   * Las anotaciones y los trazos de la barra de Flujos
   * (ADR-2026-09-16-el-lienzo-toma-la-barra-de-flujos). El lienzo los tiene en su estado
   * de React Flow y los sube acá cada vez que cambian; sólo se guarda si difieren.
   */
  setAnotaciones: (anotaciones: Anotacion[]) => void;
  setTrazos: (trazos: Trazo[]) => void;
  /**
   * Sube cada vez que `lienzo` se reemplaza desde afuera (se leyó otro chat, o se
   * vació): es la señal para que el lienzo vuelva a cargar anotaciones y trazos en su
   * estado, y para que no suba los de un chat al siguiente.
   */
  versionCarga: number;
}

export function useLienzoDelChat(): LienzoDelChat {
  const chat = useChatAtlas();
  const { companyId } = useAgentEnv();
  const chatId = chat.motor.getChatId();
  const [lienzo, setLienzo] = useState<LienzoGuardado>(LIENZO_VACIO);
  const [versionCarga, setVersionCarga] = useState(0);
  // Qué chat es el que está cargado en `lienzo`: al cambiar, se vuelve a leer.
  const cargadoParaRef = useRef<string | null>(null);
  const sucioRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lienzoRef = useRef(lienzo);
  lienzoRef.current = lienzo;
  const chatIdRef = useRef(chatId);
  chatIdRef.current = chatId;
  // Desmontado, nada más se guarda: una promesa que resuelve tarde escribiría una foto
  // vieja del lienzo encima de lo que la instancia nueva ya guardó.
  const montadoRef = useRef(true);
  // Las tandas cuya promesa sigue viva en ESTA sesión: a esas no las busca el reanudador.
  const enVueloRef = useRef<Set<string>>(new Set());

  /**
   * Escribe un lienzo en el chat que le corresponde. El id va por parámetro y no por
   * `chatIdRef` porque el flush al cambiar de chat guarda el ANTERIOR, cuando el ref ya
   * apunta al nuevo (ADR-2026-09-18-el-lienzo-se-va-con-su-chat).
   *
   * Si la escritura falla, vuelve a marcarse sucio y el próximo guardado lo recupera: el
   * lienzo se escribe entero cada vez, así que no hay nada que reconciliar. Sólo si
   * seguimos en ese chat — marcar sucio estando en otro le bloquearía la lectura al que
   * está cargando (el guard de abajo) y terminaría guardándolo vacío.
   */
  const guardarEn = useCallback((id: string, lienzo: LienzoGuardado) => {
    sucioRef.current = false;
    void guardarLienzo(id, lienzo).then((ok) => {
      if (!ok && montadoRef.current && chatIdRef.current === id) sucioRef.current = true;
    });
  }, []);

  const persistir = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    const id = chatIdRef.current;
    if (!id || !sucioRef.current || !montadoRef.current) return;
    guardarEn(id, lienzoRef.current);
  }, [guardarEn]);

  /**
   * Lo pendiente del chat que se está dejando, guardado con SU id antes de arrancar con
   * el siguiente. Sin esto, mover algo y cambiar de chat dentro de los 600 ms del
   * debounce perdía el movimiento: el timer seguía armado pero al disparar se encontraba
   * con el chat nuevo y con `sucioRef` ya en limpio, y no escribía nada.
   */
  const flushDelAnterior = useCallback(() => {
    const anterior = cargadoParaRef.current;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    if (!anterior || !sucioRef.current || !montadoRef.current) return;
    // `lienzoRef` todavía tiene el del anterior: el `setLienzo` de este efecto no corrió.
    guardarEn(anterior, lienzoRef.current);
  }, [guardarEn]);

  useEffect(() => {
    if (!chatId) {
      // Chat nuevo (reiniciar): sin id, el lienzo vuelve a vacío. Lo que el chat anterior
      // tuviera sin guardar se va con su flush, igual que al cambiar de chat.
      flushDelAnterior();
      cargadoParaRef.current = null;
      setLienzo(LIENZO_VACIO);
      setVersionCarga((v) => v + 1);
      return;
    }
    if (cargadoParaRef.current === chatId) return;
    // Cambió el chat: lo pendiente del anterior se guarda con SU id (`cargadoParaRef`
    // todavía lo tiene) y recién ahí se arranca limpio y se lee lo de éste. Si el usuario
    // mueve algo antes de que la lectura vuelva, lo suyo manda (`sucioRef`) y la lectura
    // tardía no lo pisa.
    flushDelAnterior();
    cargadoParaRef.current = chatId;
    sucioRef.current = false;
    setLienzo(LIENZO_VACIO);
    setVersionCarga((v) => v + 1);
    // Sin bandera `vivo` en la limpieza: StrictMode limpia y vuelve a correr el efecto
    // al montar, y con ella la segunda corrida se saltaba la lectura (el chat ya figuraba
    // como cargado) y la primera descartaba lo leído — el lienzo guardado no se cargaba
    // nunca en desarrollo (bug del 2026-09-16). Alcanza con que el chat siga siendo éste
    // y que el usuario no haya tocado nada mientras tanto; desmontado, `setLienzo` no
    // hace nada.
    leerLienzo(chatId)
      .then((raw) => {
        if (chatIdRef.current === chatId && !sucioRef.current) {
          setLienzo(normalizarLienzo(raw));
          setVersionCarga((v) => v + 1);
        }
      })
      .catch(() => {});
  }, [chatId, flushDelAnterior]);

  const programar = useCallback(() => {
    sucioRef.current = true;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(persistir, DEBOUNCE_MS);
  }, [persistir]);

  // Flush al desmontar: lo último movido no se pierde por navegar. `montadoRef` se
  // prende en el cuerpo del efecto y no sólo en el `useRef` inicial: StrictMode monta,
  // desmonta y vuelve a montar en desarrollo, y con el apagado sólo en la limpieza el
  // hook quedaba "desmontado" para siempre y no guardaba nada (bug del 2026-09-16).
  useEffect(() => {
    montadoRef.current = true;
    return () => {
      persistir();
      montadoRef.current = false;
    };
  }, [persistir]);

  const moverNodos = useCallback(
    (nuevas: Posiciones) => {
      setLienzo((l) => conPosiciones(l, nuevas));
      programar();
    },
    [programar],
  );
  const setOculto = useCallback(
    (oculto: boolean) => {
      setLienzo((l) => ({ ...l, oculto }));
      programar();
    },
    [programar],
  );
  const setAnotaciones = useCallback(
    (anotaciones: Anotacion[]) => {
      if (JSON.stringify(anotaciones) === JSON.stringify(lienzoRef.current.anotaciones)) return;
      setLienzo((l) => ({ ...l, anotaciones }));
      programar();
    },
    [programar],
  );
  const setTrazos = useCallback(
    (trazos: Trazo[]) => {
      if (JSON.stringify(trazos) === JSON.stringify(lienzoRef.current.trazos)) return;
      setLienzo((l) => ({ ...l, trazos }));
      programar();
    },
    [programar],
  );
  const colocadorRef = useRef<Colocador | null>(null);
  const fijarColocador = useCallback((c: Colocador | null) => {
    colocadorRef.current = c;
  }, []);
  const relanzadorRef = useRef<Relanzador | null>(null);
  const fijarRelanzador = useCallback((r: Relanzador | null) => {
    relanzadorRef.current = r;
  }, []);
  // La promesa de una tanda en vuelo resuelve o quita la tanda. Resolvió (o falló) con
  // el chat cambiado o la pantalla cerrada: no se toca este estado; la tanda quedó
  // guardada como pendiente y el reanudador la completa —o la vence— cuando ese chat se
  // vuelva a abrir.
  const engancharResultado = useCallback(
    (tandaId: string, resultado: Promise<string[]>) => {
      const chatDeLaTanda = chatIdRef.current;
      const sigueAca = () => montadoRef.current && chatIdRef.current === chatDeLaTanda;
      resultado.then(
        (ids) => {
          enVueloRef.current.delete(tandaId);
          if (!sigueAca()) return;
          setLienzo((l) => (ids.length === 0 ? sinTanda(l, tandaId) : conTandaResuelta(l, tandaId, ids)));
          programar();
        },
        () => {
          enVueloRef.current.delete(tandaId);
          if (!sigueAca()) return;
          setLienzo((l) => sinTanda(l, tandaId));
          programar();
        },
      );
    },
    [programar],
  );
  const agregarTanda = useCallback(
    (nueva: NuevaTandaDelLienzo) => {
      // El lugar se decide AHORA, una vez, con lo que el lienzo ve: sin esto la tanda se
      // recalculaba debajo de su foto de origen en cada acomodo y la seguía al moverla
      // (ADR-2026-09-18-las-fotos-no-se-mueven-juntas).
      const posicion = nueva.posicion ?? colocadorRef.current?.(nueva.origenId, nueva.total);
      const tanda: TandaGuardada = {
        id: `visor-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
        titulo: nueva.titulo,
        creadaEn: Date.now(),
        ratio: nueva.ratio,
        esVideo: nueva.esVideo,
        ...(nueva.origenId ? { origenId: nueva.origenId } : {}),
        ...(posicion ? { posicion } : {}),
        imageIds: [],
        pendiente: {
          total: nueva.total,
          ...(nueva.executionId ? { executionId: nueva.executionId } : {}),
          ...(nueva.executionIds && nueva.executionIds.length > 0 ? { executionIds: nueva.executionIds } : {}),
          ...(nueva.pedido ? { pedido: nueva.pedido } : {}),
        },
      };
      enVueloRef.current.add(tanda.id);
      setLienzo((l) => conTanda(l, tanda));
      programar();
      engancharResultado(tanda.id, nueva.resultado);
      return tanda.id;
    },
    [programar, engancharResultado],
  );
  const setDiseno = useCallback(
    (fn: (d: DisenoGuardado) => DisenoGuardado) => {
      setLienzo((l) => conDiseno(l, fn));
      programar();
    },
    [programar],
  );
  // REANUDAR: las tandas guardadas como pendientes cuya promesa no está en esta sesión.
  // Se mira al abrir y cada tanto mientras el chat está abierto. La que ya tiene todo lo
  // que esperaba (contando lo que falló) se resuelve con lo que salió; la que el backend
  // nunca recibió —la página se recargó mientras la visión escribía el prompt— se
  // relanza con los mismos ids si guardó su pedido, y si no se va; la que el backend
  // sigue corriendo espera aunque haya vencido; la vencida sin noticias se resuelve con
  // lo que haya o se va (ADR-2026-09-18-una-tanda-en-vuelo-se-relanza-si-el-pedido-no-llego).
  const hayPendientes = lienzo.tandas.some((t) => t.pendiente);
  // Lo último que dijo el backend de cada tanda, para no preguntarle en cada vuelta.
  const estadoDelPedidoRef = useRef<Map<string, { en: number; estado: EstadoDelPedido }>>(new Map());
  useEffect(() => {
    if (!chatId || !companyId || !hayPendientes) return;
    let vivo = true;
    const revisar = async () => {
      const ahora = Date.now();
      for (const t of lienzoRef.current.tandas) {
        if (!t.pendiente || enVueloRef.current.has(t.id)) continue;
        const encontrado = await buscarResultadosDeTanda(companyId, t);
        if (!vivo) return;
        const ids = elegirResultados(encontrado.ids, idsUsados(lienzoRef.current.tandas, t.id), t.pendiente.total);
        let estado: EstadoDelPedido | null = null;
        if (necesitaEstadoDelPedido(t, encontrado, ahora)) {
          const previo = estadoDelPedidoRef.current.get(t.id);
          if (previo && ahora - previo.en < INTERVALO_ESTADO_MS) {
            estado = previo.estado;
          } else {
            const consulta = consultaDeTanda(t);
            estado = await consultarEstadoDelPedido(consulta?.por === 'execution' ? consulta.executionIds : []);
            if (!vivo) return;
            estadoDelPedidoRef.current.set(t.id, { en: ahora, estado });
          }
        }
        const decision = decidirReanudacion(t, { ids, fallidas: encontrado.fallidas }, estado, ahora);
        if (decision === 'esperar') continue;
        estadoDelPedidoRef.current.delete(t.id);
        if (decision === 'relanzar') {
          const resultado = relanzadorRef.current?.(t) ?? null;
          if (resultado) {
            enVueloRef.current.add(t.id);
            engancharResultado(t.id, resultado);
            continue;
          }
        }
        setLienzo((l) => (decision === 'resolver' ? conTandaResuelta(l, t.id, ids) : sinTanda(l, t.id)));
        programar();
      }
    };
    void revisar();
    const timer = setInterval(() => void revisar(), INTERVALO_MS);
    return () => {
      vivo = false;
      clearInterval(timer);
    };
  }, [chatId, companyId, hayPendientes, programar, engancharResultado]);

  // Los nombres del catálogo, para el rótulo de cada foto (producto o modelo).
  const { productos, modelos } = useEntornoChatAtlas();
  const nombres = useMemo<NombresDelCatalogo>(
    () => ({
      producto: (id) => productos.find((p) => String(p.id) === id)?.name,
      modelo: (id) => modelos.find((m) => String(m.id) === id)?.nombre,
    }),
    [productos, modelos],
  );
  // Primero las de los mensajes, después las del visor en el orden en que se pidieron:
  // así la foto de origen de cada tanda derivada ya está colocada cuando el acomodo la
  // busca. Las pendientes muestran sus recuadros.
  const tandas = useMemo(
    () => [...tandasDeMensajes(chat.mensajes, nombres), ...lienzo.tandas.map((t) => tandaDeGuardada(t))],
    [chat.mensajes, nombres, lienzo.tandas],
  );

  return {
    tandas, lienzo, moverNodos, setOculto, agregarTanda, fijarColocador, fijarRelanzador, setDiseno, setAnotaciones, setTrazos, versionCarga,
  };
}
